import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import PremiumDarkAtmosphere from './PremiumDarkAtmosphere';

/**
 * ResultsTensionGate — Cinematic dark "moment of tension" inserted RIGHT BEFORE a CTA
 * (Ebook 47€ or WhatsApp coaching conversation) on the audit results page.
 *
 * Purpose:
 *  - Pause the user
 *  - Frame the choice with weight ("Tu as 2 chemins...")
 *  - Make the CTA below feel EARNED, not random
 *
 * Variants:
 *  - 'ebook'    → "Le 1er pas — tu agis seul avec un plan"
 *  - 'coaching' → "Le raccourci — tu prends 15 min avec moi"
 */

const VARIANTS = {
  ebook: {
    kicker: '📘 Option 1 · Tu agis seul',
    title: ['LE PREMIER PAS,', 'EN AUTONOMIE.'],
    sub: (
      <>
        Tu pars avec un <strong className="text-white">plan d'action 30 jours</strong> chiffré pour ton resto.
        <br />
        47€. Aucun appel. Aucun engagement. Juste l'exécution.
      </>
    ),
    accentFrom: '#06b6d4',
    accentTo: '#0891b2',
    chevronLabel: 'Voir le plan 47€',
    scrollTargetSelector: '[data-testid="ebook-sales-section"], [data-testid="ebook-block-cover"]',
    proofPoints: [
      { ic: '⚡', txt: 'Plan exécutable en 30j' },
      { ic: '🎯', txt: 'Chiffré sur tes données' },
      { ic: '🔒', txt: 'Sans appel commercial' },
    ],
  },
  coaching: {
    kicker: '🤝 Option 2 · Accompagnement Premium',
    title: ['ON CONSTRUIT', 'TA MARQUE DURABLE.'],
    sub: (
      <>
        Tu sautes l'autodidacte. <strong className="text-white">Accompagnement 1-à-1 avec Sabril</strong> sur 90 jours ou plus.
        <br />
        11 modules · 2 à 9 jours sur place · résultats garantis.
      </>
    ),
    accentFrom: '#F05A1A',
    accentTo: '#FF7A3D',
    chevronLabel: 'Découvrir PRSNL',
    // Cross-page navigation : redirect to /prsnl instead of just scrolling
    scrollTargetSelector: null,
    redirectTo: '/prsnl',
    proofPoints: [
      { ic: '🎯', txt: '11 modules opérationnels' },
      { ic: '👨‍🍳', txt: '2-9 jours sur place' },
      { ic: '🛡️', txt: 'Résultats garantis' },
    ],
  },
  app_signup: {
    kicker: '⚡ Le moment de vérité',
    title: ['ARRÊTE DE PERDRE.', 'PILOTE.'],
    sub: (
      <>
        Chaque mois sans l'app = <strong className="text-white">7 000€ qui s'évaporent</strong> dans des marges floues, des fiches perdues, et un POS aveugle.
        <br />
        L'outil que t'aurais dû avoir depuis 10 ans est juste là.
      </>
    ),
    accentFrom: '#F05A1A',
    accentTo: '#FF7A3D',
    chevronLabel: 'Activer mon compte',
    scrollTargetSelector: '[data-testid="final-subscribe-btn"]',
    proofPoints: [
      { ic: '🤖', txt: '6 agents IA actifs J+1' },
      { ic: '📈', txt: 'ROI x20 dès 1er mois' },
      { ic: '🔓', txt: 'Sans engagement' },
    ],
  },
};

const ResultsTensionGate = ({
  variant = 'ebook',
  testid,
  // --- Personalization overrides (optional) ---
  customKicker,
  customTitle,      // [string, string] — 2 lines
  customSub,        // ReactNode
  customProofPoints, // array of { ic, txt }
  customChevronLabel,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const baseV = VARIANTS[variant] || VARIANTS.ebook;
  // Merge: any provided custom* override its baseV equivalent
  const v = {
    ...baseV,
    kicker: customKicker || baseV.kicker,
    title: customTitle && customTitle.length === 2 ? customTitle : baseV.title,
    sub: customSub || baseV.sub,
    proofPoints: customProofPoints && customProofPoints.length === 3 ? customProofPoints : baseV.proofPoints,
    chevronLabel: customChevronLabel || baseV.chevronLabel,
  };
  const finalTestid = testid || `results-tension-gate-${variant}`;

  const scrollToCta = () => {
    // Cross-page redirect takes priority over scroll
    if (v.redirectTo) {
      window.location.href = v.redirectTo;
      return;
    }
    const target = document.querySelector(v.scrollTargetSelector);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={ref}
      className="relative overflow-hidden rounded-3xl my-6 sm:my-8 py-12 sm:py-14 px-6 border border-white/10"
      style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      data-testid={finalTestid}
    >
      {/* Premium atmospheric layer */}
      <PremiumDarkAtmosphere variant={variant === 'app_signup' || v.redirectTo ? 'orange' : 'default'} vignette={false} />

      <div className="relative text-center max-w-xl mx-auto">
        {/* Kicker */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-[10px] sm:text-xs font-black uppercase tracking-[3px] mb-4"
          style={{ color: v.accentFrom }}
        >
          {v.kicker}
        </motion.p>

        {/* Title — 2 lines, second line gradient */}
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-1"
        >
          {v.title[0]}
        </motion.h2>
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6"
          style={{
            background: `linear-gradient(135deg, ${v.accentFrom} 0%, ${v.accentTo} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {v.title[1]}
        </motion.h2>

        {/* Subline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-white/65 text-sm sm:text-base leading-relaxed mb-7"
        >
          {v.sub}
        </motion.p>

        {/* 3 proof points (illustrative card row) */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-7">
          {v.proofPoints.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
              className="rounded-xl p-2.5 sm:p-3 bg-white/[0.04] border border-white/10 backdrop-blur-sm"
            >
              <div className="text-lg sm:text-xl mb-1">{p.ic}</div>
              <p className="text-white/75 text-[10px] sm:text-[11px] font-bold leading-tight">
                {p.txt}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Scroll cue / CTA */}
        <motion.button
          type="button"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.4, delay: 1.05 }}
          onClick={scrollToCta}
          className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 transition-all"
          data-testid={`${finalTestid}-cue`}
        >
          <span className="text-white/85 text-[11px] sm:text-xs font-bold uppercase tracking-widest group-hover:text-white">
            {v.chevronLabel}
          </span>
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ color: v.accentFrom }}
          >
            <ChevronDown className="w-4 h-4" />
          </motion.span>
        </motion.button>
      </div>
    </section>
  );
};

export default ResultsTensionGate;
